import React, { useState, useEffect } from 'react';
import { Container, Typography, Box, Button, MenuItem, Select, InputLabel, FormControl, Paper, TextField, CircularProgress, Alert, List, ListItem, ListItemText, ThemeProvider, createTheme, CssBaseline, Card, Stack, Divider, Avatar, Snackbar, Alert as MuiAlert, Autocomplete, LinearProgress, useMediaQuery, Tooltip, Dialog, DialogTitle, DialogContent, IconButton, AppBar, Toolbar, Menu, ToggleButtonGroup, ToggleButton, Grid, Chip } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import axios from 'axios';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip as ChartTooltip, Legend } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import * as XLSX from 'xlsx';
import { AccountCircle, Logout, Chat, Send, Analytics, TableChart, CardGiftcard, ContentCut } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import { inject } from '@vercel/analytics';
import { DataGrid } from '@mui/x-data-grid';
import { format } from 'date-fns';
import Dashboard from './Dashboard';
import Subscription from './Subscription';
import ExcelTemplates from './ExcelTemplates';
import HowItWorks from './HowItWorks';
import { useAuth } from './AuthContext';
import Login from './Login';
import AdminAnalytics from './AdminAnalytics';
import PrivacyPolicyPage from './PrivacyPolicyPage';
import DeleteAccountPage from './DeleteAccountPage';
import DeleteDataPage from './DeleteDataPage';
import SplitPdfPage from './SplitPdfPage';
import MobileAppCta from './MobileAppCta';
import {
  ANALYZER_MAX_PAGES_PER_FILE,
  ANALYZER_SPLIT_PDF_GUIDANCE,
  LARGE_PDF_LOADING_HINT,
} from './uploadHelpText';
import tracker from './activityTracker';
import { typedTransactionsSheet, typedTopEntitiesSheet } from './utils/excelAnalysisExport';
import { getUploadErrorMessage } from './utils/uploadPdfErrors';

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, ChartTooltip, Legend);

inject();

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: '#43a047' }, // Green accent
    
    background: { default: '#f7fafc', paper: '#fff' },
  },
  shape: { borderRadius: 12 },
  typography: { fontFamily: 'Inter, Roboto, Arial, sans-serif' },
});

// Define a diverse color palette for up to 10 entities (moved outside component to prevent re-creation on each render)
const COLORS = [
  '#1976d2', // Blue
  '#e91e63', // Pink
  '#ff9800', // Orange
  '#4caf50', // Green
  '#9c27b0', // Purple
  '#f44336', // Red
  '#00bcd4', // Cyan
  '#ffeb3b', // Yellow
  '#795548', // Brown
  '#607d8b'  // Blue Grey
];

function App() {
  const { user, logout, loading: authLoading, isAuthenticated, getAuthHeaders, getDisplayName, getDisplayInitial, verifyEmailConfirmation } = useAuth();
  
  // Initialize currentTab from localStorage or default to 0
  const getInitialTab = () => {
    if (window.location.pathname.toLowerCase() === '/login') {
      return 1;
    }
    const savedTab = localStorage.getItem('currentTab');
    const tabValue = savedTab ? parseInt(savedTab) : 0;
    
    // Allow any tab value - the Dashboard component will handle authentication
    return tabValue;
  };
  
  const [currentTab, setCurrentTab] = useState(getInitialTab);
  const [emailVerificationLoading, setEmailVerificationLoading] = useState(false);

  useEffect(() => {
    const TAB_NAMES = ['analyzer', 'dashboard', 'how_it_works', 'subscription', 'templates'];
    tracker.trackPageView(TAB_NAMES[currentTab] || 'analyzer');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Effect to handle return tab redirects after login
  useEffect(() => {
    if (isAuthenticated) {
      // Check if there's a return tab stored (e.g., from templates page after login)
      const returnToTab = localStorage.getItem('returnToTab');
      if (returnToTab) {
        const tabNumber = parseInt(returnToTab);
        handleTabChange(tabNumber); // Redirect to the stored tab (e.g., templates page)
        localStorage.removeItem('returnToTab'); // Clear the stored tab
      }
    }
  }, [isAuthenticated]); // Only run when authentication state changes

  // Effect to verify email confirmation when switching to dashboard tab
  // Note: Plans tab is accessible to everyone, so don't verify email for it
  useEffect(() => {
    const verifyDashboardAccess = async () => {
      if (currentTab === 1 && isAuthenticated) {
        setEmailVerificationLoading(true);
        try {
          const { confirmed } = await verifyEmailConfirmation();
          if (!confirmed) {
            handleTabChange(0);
            // Sign out user if email not confirmed
            await logout();
          }
        } catch (err) {
          console.error('Error verifying email confirmation:', err);
          handleTabChange(0);
        } finally {
          setEmailVerificationLoading(false);
        }
      }
    };

    verifyDashboardAccess();
  }, [currentTab, isAuthenticated, verifyEmailConfirmation, logout]);
  
  const TAB_NAMES = ['analyzer', 'dashboard', 'how_it_works', 'subscription', 'templates'];

  // Save tab to localStorage whenever it changes
  const handleTabChange = (newTab) => {
    setCurrentTab(newTab);
    localStorage.setItem('currentTab', newTab.toString());
    tracker.trackPageView(TAB_NAMES[newTab] || `tab_${newTab}`);
  };
  

  
  const [files, setFiles] = useState([]);
  const [period, setPeriod] = useState('all');
  const [paidType, setPaidType] = useState('withdrawn');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [progressMessage, setProgressMessage] = useState('');
  const [customStart, setCustomStart] = useState(null);
  const [customEnd, setCustomEnd] = useState(null);
  const [search, setSearch] = useState('');
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [selectedEntities, setSelectedEntities] = useState([]);
  const [termsOpen, setTermsOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [isPaidUser, setIsPaidUser] = useState(false);
  const [analyzerMode, setAnalyzerMode] = useState('analysis'); // 'analysis' | 'convert' | 'split'
  const [convertFormat, setConvertFormat] = useState('excel'); // 'excel' | 'csv'
  const [conversionNotice, setConversionNotice] = useState(null);
  const [splitPdfFile, setSplitPdfFile] = useState(null);
  const [splitPagesPerPart, setSplitPagesPerPart] = useState(String(ANALYZER_MAX_PAGES_PER_FILE));
  const [splitLoading, setSplitLoading] = useState(false);
  const [splitError, setSplitError] = useState('');
  const [splitSuccess, setSplitSuccess] = useState('');
  const [chartGranularity, setChartGranularity] = useState('daily');

  // Check subscription status for authenticated users
  useEffect(() => {
    const checkSubscriptionStatus = async () => {
      if (isAuthenticated && user) {
        try {
          const API_URL = process.env.REACT_APP_API_URL || 'https://mpesa-statements-dash.onrender.com';
          const authHeaders = await getAuthHeaders();
          const response = await axios.get(`${API_URL}/subscription/status`, { headers: authHeaders });
          if (response.data && response.data.plan_id && response.data.plan_id !== 'free') {
            setIsPaidUser(true);
          } else {
            setIsPaidUser(false);
          }
        } catch (error) {
          console.error('Error checking subscription status:', error);
          setIsPaidUser(false);
        }
      } else {
        setIsPaidUser(false);
      }
    };
    
    checkSubscriptionStatus();
  }, [isAuthenticated, user, getAuthHeaders]);
  const isMobile = useMediaQuery('(max-width:600px)');

  const [password, setPassword] = useState(''); // New state for PDF password
  const [youtubeVideoId] = useState('https://youtu.be/yzqeNR6gC9g'); // YouTube video ID for embedding

  useEffect(() => {
    if (analyzerMode !== 'split') {
      setSplitSuccess('');
      setSplitError('');
    } else {
      setError('');
    }
  }, [analyzerMode]);

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles(selectedFiles);
    setConversionNotice(null);
    setMinAmount('');
    setMaxAmount('');
  };

  const handleAnalyze = async () => {
    if (!files || files.length === 0) {
      setError('Please upload at least one PDF file.');
      return;
    }
    tracker.trackFeatureUse('analyze_statement', 'analyzer', { file_count: files.length });
    setError('');
    setLoading(true);
    setResult(null);
    setProgressMessage('Preparing files for analysis...');
    
    const startTime = Date.now();
    let progressInterval;
    
    // Update progress message based on elapsed time
    progressInterval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const seconds = Math.floor(elapsed / 1000);
      
      if (seconds < 5) {
        setProgressMessage('Uploading and validating PDF files...');
      } else if (seconds < 15) {
        setProgressMessage('Extracting data from PDF pages...');
      } else if (seconds < 30) {
        setProgressMessage('Processing transactions and analyzing data...');
      } else if (seconds < 60) {
        setProgressMessage('Still processing... This may take a moment for large files.');
      } else {
        setProgressMessage('Processing large file... Please wait, this may take a few minutes.');
      }
    }, 2000); // Update every 2 seconds
    
    try {
      const formData = new FormData();
      // Append all files
      files.forEach(file => {
        formData.append('files', file);
      });
      formData.append('paid_type', paidType);
      formData.append('period', period);
      if (period === 'day') {
        formData.append('date', format(selectedDate, 'dd.MM.yyyy'));
      }
      if (period === 'custom' && customStart && customEnd) {
        formData.append('custom_start', format(customStart, 'dd.MM.yyyy'));
        formData.append('custom_end', format(customEnd, 'dd.MM.yyyy'));
      }
      if (minAmount !== '') {
        formData.append('min_amount', minAmount);
      }
      if (maxAmount !== '') {
        formData.append('max_amount', maxAmount);
      }
      // Add selected entities for backend chart inclusion
      if (selectedEntities && selectedEntities.length > 0) {
        selectedEntities.forEach(e => formData.append('selected_entities', e));
      }
      formData.append('password', password); // Always send password (can be empty)
      formData.append('save_to_dashboard', false); // Use Dashboard to save analyses
      const API_URL = process.env.REACT_APP_API_URL || 'https://mpesa-statements-dash.onrender.com';
      
      // Always include auth token when authenticated (needed for quota, daily pass, subscriptions)
      let headers = {};
      if (isAuthenticated) {
        try {
          const authHeaders = await getAuthHeaders();
          if (authHeaders.Authorization) {
            headers = { ...headers, ...authHeaders };
          }
        } catch (error) {
          console.error('Error getting auth headers:', error);
        }
      }
      setProgressMessage('Sending files to server...');
      
      const response = await axios.post(`${API_URL}/upload-pdf/`, formData, {
        headers,
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setProgressMessage(`Uploading files... ${percentCompleted}%`);
          }
        },
      });
      
      setProgressMessage('Finalizing analysis...');
      if (response.data.error) {
        const errMsg = getUploadErrorMessage(response.data);
        setError(errMsg);
        setSnackbar({ open: true, message: errMsg, severity: 'error' });
      } else {
        setResult(response.data);
        const processingInfo = response.data.processing_info;
        const partialResult = response.data.partial_result;
        
        let successMessage = '';
        let severity = 'success';
        
        // Check if this is a partial result (quota limited)
        if (partialResult && partialResult.is_partial) {
          successMessage = partialResult.upgrade_message || 
            `Showing partial results: ${partialResult.transactions_shown.toLocaleString()} of ~${partialResult.estimated_total_transactions.toLocaleString()} transactions`;
          severity = 'warning';
          // Banner with upgrade button is shown in results section - no popup needed
        } else {
          successMessage = `Analysis complete! Processed ${processingInfo.files_processed} files with ${processingInfo.total_transactions} transactions. ${processingInfo.duplicate_transactions_removed} duplicates removed.`;
          
          // Add combination information if data was combined with existing
          if (processingInfo.combined_with_existing) {
            successMessage += ` Combined with ${processingInfo.existing_transactions_added} existing transactions.`;
          }
          const skipped = processingInfo.failed_file_details;
          if (skipped?.length) {
            const skipMsg = skipped.map((f) => f.message || f.filename).join(' ');
            successMessage += ` Warning: ${skipped.length} file(s) could not be opened — ${skipMsg}`;
            severity = 'warning';
          }
        }
        
        setSnackbar({ open: true, message: successMessage, severity });
        if (processingInfo?.failed_file_details?.length) {
          setError(getUploadErrorMessage({ failed_file_details: processingInfo.failed_file_details }));
        }
        
        // Scroll to results section after a short delay to allow rendering
        setTimeout(() => {
          const resultsSection = document.getElementById('analysis-results');
          if (resultsSection) {
            resultsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      }
    } catch (err) {
      // Check if error is about file size limit with upgrade requirement
      if (err.response && err.response.data && err.response.data.detail) {
        const errorDetail = err.response.data.detail;
        if (typeof errorDetail === 'object') {
          const baseMessage = getUploadErrorMessage(errorDetail) || errorDetail.message || 'Failed to analyze statements.';
          const upgradeMessage = errorDetail.upgrade_message ? ` ${errorDetail.upgrade_message}` : '';
          const fullMessage = `${baseMessage}${upgradeMessage}`.trim();
          setError(baseMessage);

          setSnackbar({
            open: true,
            message: fullMessage,
            severity: errorDetail.upgrade_required || errorDetail.split_required ? 'warning' : 'error'
          });
          // Snackbar shows the upgrade message - no popup needed
        } else if (typeof errorDetail === 'string') {
          setError(errorDetail);
          setSnackbar({ open: true, message: errorDetail, severity: 'error' });
        } else {
          setError('Failed to analyze statements.');
          setSnackbar({ open: true, message: 'Failed to analyze statements.', severity: 'error' });
        }
      } else {
        setError('Failed to analyze statements.');
        setSnackbar({ open: true, message: 'Failed to analyze statements.', severity: 'error' });
      }
    } finally {
      clearInterval(progressInterval);
      setLoading(false);
      setProgressMessage('');
    }
  };

  const handleConvert = async () => {
    if (!files || files.length === 0) {
      setError('Please upload at least one PDF file.');
      return;
    }
    tracker.trackFeatureUse('convert_pdf', 'analyzer', { format: convertFormat, file_count: files.length });
    setError('');
    setConversionNotice(null);
    setLoading(true);
    setProgressMessage(`Converting PDF to ${convertFormat === 'csv' ? 'CSV' : 'Excel'}...`);

    try {
      const formData = new FormData();
      files.forEach(file => formData.append('files', file));
      formData.append('password', password);
      formData.append('format', convertFormat);

      const API_URL = process.env.REACT_APP_API_URL || 'https://mpesa-statements-dash.onrender.com';
      let headers = {};
      if (isAuthenticated) {
        try {
          const authHeaders = await getAuthHeaders();
          if (authHeaders.Authorization) headers = { ...headers, ...authHeaders };
        } catch (e) {
          console.error('Error getting auth headers:', e);
        }
      }

      const response = await axios.post(`${API_URL}/convert-pdf/`, formData, {
        headers,
        responseType: 'blob',
      });

      const contentDisposition = response.headers['content-disposition'];
      const filenameMatch = contentDisposition && contentDisposition.match(/filename="?([^";\n]+)"?/);
      const ext = convertFormat === 'csv' ? 'csv' : 'xlsx';
      const filename = filenameMatch ? filenameMatch[1] : `converted_${new Date().toISOString().slice(0, 10)}.${ext}`;

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      const isPartial = response.headers['x-conversion-partial'] === 'true';
      if (isPartial) {
        const encodedMsg = response.headers['x-conversion-message'];
        const pagesProcessed = response.headers['x-pages-processed'];
        const pagesTotal = response.headers['x-pages-total'];
        const limitReason = response.headers['x-conversion-limit-reason'] || 'quota';
        let partialMsg = '';
        try {
          partialMsg = encodedMsg ? decodeURIComponent(encodedMsg) : '';
        } catch {
          partialMsg = '';
        }
        if (!partialMsg && pagesProcessed && pagesTotal) {
          partialMsg = `Partial conversion: ${pagesProcessed} of ${pagesTotal} pages exported. Upgrade or buy a Day Pass for the rest.`;
        }
        setConversionNotice({
          message: partialMsg || `${convertFormat === 'csv' ? 'CSV' : 'Excel'} downloaded — only part of the PDF was converted.`,
          pagesProcessed,
          pagesTotal,
          limitReason,
        });
        setSnackbar({
          open: true,
          message: partialMsg || `${convertFormat === 'csv' ? 'CSV' : 'Excel'} downloaded — only part of the PDF was converted due to page limits.`,
          severity: 'warning',
        });
      } else {
        setConversionNotice(null);
        setSnackbar({ open: true, message: `${convertFormat === 'csv' ? 'CSV' : 'Excel'} file downloaded successfully!`, severity: 'success' });
      }
    } catch (err) {
      if (err.response?.data) {
        let msg = 'Failed to convert PDF.';
        if (err.response.data instanceof Blob) {
          try {
            const text = await err.response.data.text();
            const parsed = JSON.parse(text);
            const d = parsed.detail;
            msg = (typeof d === 'object' && getUploadErrorMessage(d)) || (typeof d === 'string' ? d : msg);
          } catch {
            msg = 'Failed to convert PDF.';
          }
        } else if (typeof err.response.data.detail === 'object') {
          msg = getUploadErrorMessage(err.response.data.detail) || msg;
        } else if (typeof err.response.data.detail === 'string') {
          msg = err.response.data.detail;
        }
        setError(msg);
        setSnackbar({ open: true, message: msg, severity: 'error' });
      } else {
        setSnackbar({ open: true, message: 'Failed to convert PDF.', severity: 'error' });
      }
    } finally {
      setLoading(false);
      setProgressMessage('');
    }
  };

  const handleSplitPdf = async () => {
    if (!splitPdfFile) {
      setSplitError('Choose a PDF file first.');
      return;
    }
    tracker.trackFeatureUse('split_pdf', 'analyzer', {
      pages_per_part: splitPagesPerPart || String(ANALYZER_MAX_PAGES_PER_FILE),
    });
    setSplitError('');
    setSplitSuccess('');
    setError('');
    setSplitLoading(true);
    setProgressMessage('Splitting PDF and preparing your download...');

    try {
      const formData = new FormData();
      formData.append('file', splitPdfFile);
      formData.append('password', password);
      formData.append('pages_per_part', splitPagesPerPart || String(ANALYZER_MAX_PAGES_PER_FILE));

      const API_URL = process.env.REACT_APP_API_URL || 'https://mpesa-statements-dash.onrender.com';

      const res = await axios.post(`${API_URL}/split-pdf/`, formData, {
        responseType: 'blob',
        validateStatus: () => true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (res.status >= 400) {
        let msg = 'Could not split PDF.';
        try {
          const text = await res.data.text();
          const j = JSON.parse(text);
          if (typeof j.detail === 'string') msg = j.detail;
          else if (j.detail && typeof j.detail === 'object') msg = j.detail.message || JSON.stringify(j.detail);
        } catch {
          /* keep default */
        }
        throw new Error(msg);
      }

      const cd = res.headers['content-disposition'];
      let downloadName = 'statement_split.zip';
      if (cd && cd.includes('filename=')) {
        const m = cd.match(/filename="?([^";]+)"?/);
        if (m) downloadName = m[1];
      }

      const blob = new Blob([res.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = downloadName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      const okMsg =
        'Download started. Unzip the files, then use Full Analysis or Convert and upload each PDF (one part at a time works best).';
      setSplitSuccess(okMsg);
      setSnackbar({ open: true, message: okMsg, severity: 'success' });
    } catch (err) {
      const msg = err.message || 'Split failed.';
      setSplitError(msg);
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setSplitLoading(false);
      setProgressMessage('');
    }
  };

  const handleSubmitFeedback = async () => {
    if (!feedbackMessage.trim()) {
      setSnackbar({ open: true, message: 'Please enter your feedback', severity: 'warning' });
      return;
    }

    setFeedbackSubmitting(true);
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'https://mpesa-statements-dash.onrender.com';
      const response = await axios.post(`${API_URL}/feedback`, {
        message: feedbackMessage.trim(),
        user_email: user?.email || 'anonymous',
        user_id: user?.id || null,
        timestamp: new Date().toISOString(),
      });

      if (response.data.success) {
        setSnackbar({ open: true, message: 'Thank you for your feedback!', severity: 'success' });
        setFeedbackMessage('');
        setFeedbackOpen(false);
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      setSnackbar({ open: true, message: 'Failed to submit feedback. Please try again.', severity: 'error' });
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  // Ensure selected entities are always included in chart, even if not in top_entity_names
  const allChartEntityNames = React.useMemo(() => {
    if (!result || !result.top_entity_names) return [];
    
    // Filtered entity names based on search
    const filteredEntityNames = result.top_entity_names.filter(name => {
      const matchesSearch = search.trim() === '' || name.toLowerCase().includes(search.toLowerCase());
      const normalizedSelected = selectedEntities.map(e => extractName(e));
      const nameExtracted = extractName(name);
      const matchesEntity = selectedEntities.length === 0 ||
        normalizedSelected.some(sel => nameExtracted.includes(sel) || sel.includes(nameExtracted));
      return matchesSearch && matchesEntity;
    });
    
    // Start with filtered entity names
    let entities = [...filteredEntityNames];
    
    // Add any selected entities that might not be in top_entity_names
    selectedEntities.forEach(selectedEntity => {
      const normalizedSelected = extractName(selectedEntity);
      const isAlreadyIncluded = entities.some(entity => {
        const entityExtracted = extractName(entity);
        return entityExtracted.includes(normalizedSelected) || normalizedSelected.includes(entityExtracted);
      });
      
      if (!isAlreadyIncluded) {
        // Find the entity in top_entities_full that matches
        const matchingEntity = result.top_entities?.find(entity => {
          const entityExtracted = extractName(entity.details);
          return entityExtracted.includes(normalizedSelected) || normalizedSelected.includes(entityExtracted);
        });
        
        if (matchingEntity) {
          entities.push(matchingEntity.details);
        }
      }
    });
    
    return entities;
  }, [search, selectedEntities, result]);

  // Filtered stacked chart data
  const filteredStackedChartData = React.useMemo(() => {
    if (!result || !result.stacked_chart_data) return [];
    return result.stacked_chart_data.map(day => {
      const filteredDay = { date: day.date };
      allChartEntityNames.forEach(name => {
        // Ensure all selected entities have data, even if zero
        filteredDay[name] = day[name] || 0;
      });
      return filteredDay;
    });
  }, [result, allChartEntityNames]);

  // Ensure all selected entities are represented in chart data, even if they have no transactions
  const enhancedChartData = React.useMemo(() => {
    if (!filteredStackedChartData || filteredStackedChartData.length === 0) return [];
    
    // For each selected entity, ensure it has data for all dates
    const enhancedData = filteredStackedChartData.map(day => {
      const enhancedDay = { ...day };
      
      // Ensure all chart entities have data for this day
      allChartEntityNames.forEach(entityName => {
        if (enhancedDay[entityName] === undefined) {
          enhancedDay[entityName] = 0;
        }
      });
      
      return enhancedDay;
    });
    
    return enhancedData;
  }, [filteredStackedChartData, allChartEntityNames]);

  const granularChartData = React.useMemo(() => {
    if (!enhancedChartData || !enhancedChartData.length || chartGranularity === 'daily') {
      return enhancedChartData;
    }
    const entityNames = allChartEntityNames || [];
    const buckets = {};
    enhancedChartData.forEach(day => {
      const key = chartGranularity === 'monthly' ? day.date.slice(0, 7) : day.date.slice(0, 4);
      if (!buckets[key]) {
        buckets[key] = { date: key, total_amount: 0, transaction_count: 0 };
        entityNames.forEach(name => { buckets[key][name] = 0; });
      }
      buckets[key].total_amount += day.total_amount || 0;
      buckets[key].transaction_count += day.transaction_count || 0;
      entityNames.forEach(name => { buckets[key][name] += day[name] || 0; });
    });
    return Object.values(buckets).sort((a, b) => a.date.localeCompare(b.date));
  }, [enhancedChartData, chartGranularity, allChartEntityNames]);

  // Filtered total for chart (sum of all visible bars)
  const filteredTotal = granularChartData && granularChartData.length > 0
    ? granularChartData.reduce((sum, day) => {
        return sum + allChartEntityNames.reduce((s, name) => s + (day[name] || 0), 0);
      }, 0)
    : 0;

  // Transform data for Chart.js Bar Chart
  const chartJsBarData = React.useMemo(() => {
    if (!granularChartData || !granularChartData.length || !allChartEntityNames) {
      return { labels: [], datasets: [] };
    }

    const formatLabel = (d) => {
      if (chartGranularity === 'monthly') {
        const [y, m] = d.split('-');
        const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
        return `${months[parseInt(m, 10) - 1]} ${y}`;
      }
      if (chartGranularity === 'yearly') return d;
      return d;
    };

    const labels = granularChartData.map(day => formatLabel(day.date));
    const datasets = allChartEntityNames.map((entityName, index) => ({
      label: entityName,
      data: granularChartData.map(day => day[entityName] || 0),
      backgroundColor: COLORS[index % COLORS.length],
      borderColor: COLORS[index % COLORS.length],
      borderWidth: 1,
      stack: 'stack0'
    }));

    return { labels, datasets };
  }, [granularChartData, allChartEntityNames, chartGranularity]);

  // Transform data for Chart.js Line Chart (unused but kept for potential future use)
  // const chartJsLineData = React.useMemo(() => {
  //   if (!filteredLineChartData || !filteredLineChartData.length) {
  //     return { labels: [], datasets: [] };
  //   }
  //
  //   const labels = filteredLineChartData.map(day => day.date);
  //   const datasets = [{
  //     label: `Transaction Count`,
  //     data: filteredLineChartData.map(day => day.count),
  //     borderColor: '#1976d2',
  //     backgroundColor: 'rgba(25, 118, 210, 0.1)',
  //     borderWidth: 3,
  //     fill: true,
  //     tension: 0.4
  //   }];
  //
  //   return { labels, datasets };
  // }, [filteredLineChartData]);

  // Chart.js options for Bar Chart
  const chartJsBarOptions = React.useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false, // Hide legend to prevent chart squishing on mobile
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          label: function(context) {
            const value = context.parsed.y;
            return `${context.dataset.label}: ${new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'KES',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            }).format(value)}`;
          }
        }
      }
    },
    scales: {
      x: {
        stacked: true,
        grid: {
          display: false
        },
        ticks: {
          maxRotation: 45,
          minRotation: 0,
          font: {
            size: 11
          }
        }
      },
      y: {
        stacked: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.1)'
        },
        ticks: {
          callback: function(value) {
            if (value >= 1000000) {
              return `${(value / 1000000).toFixed(1)}M`;
            } else if (value >= 1000) {
              return `${(value / 1000).toFixed(0)}K`;
            }
            return value.toLocaleString();
          },
          font: {
            size: 11
          }
        }
      }
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false
    }
  }), []);

  // Chart.js options for Line Chart (unused but kept for potential future use)
  // const chartJsLineOptions = React.useMemo(() => ({
  //   responsive: true,
  //   maintainAspectRatio: false,
  //   plugins: {
  //     legend: {
  //       display: false, // Hide legend to prevent chart squishing on mobile
  //     },
  //     tooltip: {
  //       mode: 'index',
  //       intersect: false,
  //       callbacks: {
  //         label: function(context) {
  //           const value = context.parsed.y;
  //           return `${context.dataset.label}: ${value.toLocaleString()}`;
  //         }
  //       }
  //     }
  //   },
  //   scales: {
  //     x: {
  //       grid: {
  //         display: false
  //       },
  //       ticks: {
  //         maxRotation: 45,
  //         minRotation: 0,
  //         font: {
  //           size: 11
  //         }
  //       }
  //     },
  //     y: {
  //       grid: {
  //         color: 'rgba(0, 0, 0, 0.1)'
  //       },
  //       ticks: {
  //         callback: function(value) {
  //           return value.toLocaleString();
  //         },
  //         font: {
  //           size: 11
  //         }
  //       }
  //     }
  //   },
  //   interaction: {
  //     mode: 'nearest',
  //     axis: 'x',
  //     intersect: false
  //   }
  // }), []);

  // Filtered top entities for the list
  const filteredTopEntities = result && result.top_entities
    ? result.top_entities.filter(entity => {
        const matchesEntity = selectedEntities.length === 0 || selectedEntities.includes(entity.details);
        const matchesSearch = search.trim() === '' || entity.details.toLowerCase().includes(search.toLowerCase());
        return matchesEntity && matchesSearch;
      })
    : [];

  function extractName(details) {
    // Get the part after the last dash, trim, and lowercase
    const parts = details.split('-');
    return parts.length > 1 ? parts[parts.length - 1].toLowerCase().trim() : details.toLowerCase().trim();
  }

  // Remove filteredPreviewRows and use all rows for the DataGrid
  // Filtered preview rows for the table
  const filteredPreviewRows = result && result.rows && result.columns && result.top_entities
    ? result.rows.filter(row => {
        const detailsIdx = result.columns.findIndex(col => col === 'details');
        if (detailsIdx === -1) return true;
        const detailsVal = (row[detailsIdx] || '').toString();
        const detailsName = extractName(detailsVal);
        const normalizedSelected = selectedEntities.map(e => extractName(e));
        const matchesEntity = selectedEntities.length === 0 ||
          normalizedSelected.some(sel => detailsName.includes(sel) || sel.includes(detailsName));
        const matchesSearch = search.trim() === '' || detailsName.includes(search.toLowerCase().trim());
        return matchesEntity && matchesSearch;
      })
    : [];

  // Calculate Daily (Avg.), Weekly (Avg.), Monthly (Avg.) for the chart - using filtered data
  let dayAvg = 0, weekAvg = 0, monthAvg = 0;
  if (enhancedChartData && enhancedChartData.length > 0) {
    const totalsByDay = enhancedChartData.map(day =>
      allChartEntityNames.reduce((s, name) => s + (day[name] || 0), 0)
    );
    dayAvg = totalsByDay.reduce((a, b) => a + b, 0) / totalsByDay.length;
    
    // Calculate weekly average (group by weeks)
    const weekMap = new Map();
    
    enhancedChartData.forEach(day => {
      const date = new Date(day.date);
      const weekStart = new Date(date);
      weekStart.setDate(date.getDate() - date.getDay()); // Start of week (Sunday)
      weekStart.setHours(0, 0, 0, 0);
      
      const weekKey = weekStart.toISOString().slice(0, 10);
      if (!weekMap.has(weekKey)) {
        weekMap.set(weekKey, 0);
      }
      weekMap.set(weekKey, weekMap.get(weekKey) + allChartEntityNames.reduce((s, name) => s + (day[name] || 0), 0));
    });
    
    weekAvg = Array.from(weekMap.values()).reduce((a, b) => a + b, 0) / weekMap.size;
    
    // Calculate monthly average (group by months)
    const monthMap = new Map();
    
    enhancedChartData.forEach(day => {
      const date = new Date(day.date);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      
      if (!monthMap.has(monthKey)) {
        monthMap.set(monthKey, 0);
      }
      monthMap.set(monthKey, monthMap.get(monthKey) + allChartEntityNames.reduce((s, name) => s + (day[name] || 0), 0));
    });
    
    monthAvg = Array.from(monthMap.values()).reduce((a, b) => a + b, 0) / monthMap.size;
  }

  // Export to Excel handler
  const handleExportExcel = () => {
    if (!result || !result.columns || !result.all_rows || !result.all_rows.length) return;
    tracker.trackFeatureUse('export_excel', 'analyzer');
    // Find the index of the 'completion_time' column
    const completionTimeIdx = result.columns.findIndex(col => col === 'completion_time');
    // Prepare new columns: replace 'completion_time' with 'date' and 'time'
    let exportColumns = [...result.columns];
    if (completionTimeIdx !== -1) {
      exportColumns = [
        ...result.columns.slice(0, completionTimeIdx),
        'date',
        'time',
        ...result.columns.slice(completionTimeIdx + 1)
      ];
    }
    // Filter all_rows by search and selectedEntities (match preview table logic)
    const detailsIdx = result.columns.findIndex(col => col === 'details');
    // Get indexes of 'balance' and 'bankreferencenumber' if they exist
    const balanceIdx = exportColumns.indexOf('balance');
    const bankRefIdx = exportColumns.indexOf('bankreferencenumber');

    // Get the later index (if any exist), otherwise export all columns
    const endIdx = Math.max(balanceIdx, bankRefIdx);

    const finalExportColumns = endIdx !== -1
      ? exportColumns.slice(0, endIdx + 1)
      : [...exportColumns];

    const exportRows = result.all_rows.filter(row => {
      if (detailsIdx === -1) return true;
      const detailsVal = (row[detailsIdx] || '').toString();
      const matchesEntity = selectedEntities.length === 0 || selectedEntities.includes(detailsVal);
      const matchesSearch = search.trim() === '' || detailsVal.toLowerCase().includes(search.toLowerCase());
      return matchesEntity && matchesSearch;
    }).map(row => {
      if (completionTimeIdx !== -1) {
        // Replace completion_time with date and time
                  const newRow = [
            ...row.slice(0, completionTimeIdx),
            row[completionTimeIdx] ? row[completionTimeIdx].split(' ')[0] : '',
            row[completionTimeIdx] ? row[completionTimeIdx].split(' ')[1] : '',
            ...row.slice(completionTimeIdx + 1)
          ];
        return newRow.slice(0, finalExportColumns.length);
      }
      return row.slice(0, finalExportColumns.length);
    });

    const totalSum = filteredTopEntities.reduce((sum, e) => sum + e.total, 0);
    const topEntitiesRows = filteredTopEntities.map(entity => {
      const percent = totalSum > 0 ? (entity.total / totalSum) * 100 : 0;
      return [entity.details, entity.total, percent];
    });

    const wsTransactions = typedTransactionsSheet(finalExportColumns, exportRows);
    const wsTopEntities = typedTopEntitiesSheet(topEntitiesRows);
    
    // Create workbook with both sheets
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, wsTopEntities, 'Top People & Businesses');
    XLSX.utils.book_append_sheet(wb, wsTransactions, 'Transactions');
    
    XLSX.writeFile(wb, `Mobile_Money_Analysis_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  // All unique people/businesses for autocomplete suggestions
  const allEntityNames = result && result.top_entities
    ? Array.from(new Set(result.top_entities.map(e => e.details)))
    : [];

  // Helper to build line chart data for any entity/entities from result.rows (unused but kept for potential future use)
  // const getLineChartData = (entities) => {
  //   if (!result || !result.rows || !result.columns || !entities || entities.length === 0) return [];
  //   const detailsIdx = result.columns.findIndex(col => col === 'details');
  //   const amountIdx = result.columns.findIndex(col => col === paidType);
  //   const dateIdx = result.columns.findIndex(col => col === 'completion_time');
  //   if (detailsIdx === -1 || amountIdx === -1 || dateIdx === -1) return [];
  //   // Group by date
  //   const dateMap = {};
  //   result.rows.forEach(row => {
  //     const detailsVal = (row[detailsIdx] || '').toString();
  //     if (!entities.includes(detailsVal)) return;
  //     const dateRaw = row[dateIdx];
  //     let dateStr = '';
  //     if (dateRaw) {
  //       const d = new Date(dateRaw);
  //       if (!isNaN(d)) {
  //         dateStr = d.toISOString().slice(0, 10);
  //       } else if (typeof dateRaw === 'string' && dateRaw.length >= 10) {
  //         dateStr = dateRaw.slice(0, 10);
  //       } else {
  //         dateStr = dateRaw;
  //       }
  //     }
  //     if (!dateMap[dateStr]) dateMap[dateStr] = {};
  //     entities.forEach(name => {
  //       if (!dateMap[dateStr][name]) dateMap[dateStr][name] = 0;
  //     });
  //     dateMap[dateStr][detailsVal] += parseFloat(row[amountIdx]) || 0;
  //   });
  //   // Convert to array and sort by date
  //   return Object.entries(dateMap)
  //     .map(([date, vals]) => ({ date, ...vals }))
  //     .sort((a, b) => a.date.localeCompare(b.date));
  // };

  const mobileColumns = ["receiptno", "completion_time", "details", "paid_in", "withdrawn"];
  // Update columns for DataGrid to exclude 'transactionstatus' and only show the selected paidType
  const baseColumns = result && result.columns
    ? result.columns.filter(col => col !== 'transactionstatus' && (col === paidType || !['paid_in', 'withdrawn'].includes(col)))
    : [];
  const columns = baseColumns.map(col => {
    // Define column-specific configurations for better visibility
    let columnConfig = {
      field: col,
      headerName: col,
      sortable: true,
      filterable: true,
      hide: isMobile && !mobileColumns.includes(col),
    };

    // Set specific widths and flex values for different columns
    switch (col) {
      case 'details':
        columnConfig.flex = 3; // Make details column wider
        columnConfig.minWidth = isMobile ? 200 : 300;
        break;
      case 'completion_time':
        columnConfig.flex = 1.5;
        columnConfig.minWidth = isMobile ? 120 : 160;
        break;
      case 'paid_in':
      case 'withdrawn':
        columnConfig.flex = 1.2;
        columnConfig.minWidth = isMobile ? 100 : 120;
        break;
      case 'receiptno':
        columnConfig.flex = 1;
        columnConfig.minWidth = isMobile ? 80 : 100;
        break;
      default:
        columnConfig.flex = 1;
        columnConfig.minWidth = isMobile ? 80 : 100;
        break;
    }

    return columnConfig;
  });



  const handleDownloadChart = async () => {
    try {
      // Chart.js provides a built-in method to get the canvas
      const canvas = document.querySelector('canvas');
      if (!canvas) {
        console.error('Chart canvas not found');
        return;
      }
      
      const link = document.createElement('a');
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      link.download = `Mobile Money Analyzer_${timestamp}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (error) {
      console.error('Error downloading chart:', error);
    }
  };

  // Helper function to extract YouTube video ID from URL or return the ID if it's already just an ID
  const extractYouTubeId = (urlOrId) => {
    if (!urlOrId) return '';
    
    // If it's already just an ID (no slashes or special characters), return it
    if (!urlOrId.includes('/') && !urlOrId.includes('?') && !urlOrId.includes('&')) {
      return urlOrId;
    }
    
    // Extract ID from various YouTube URL formats
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
      /youtube\.com\/.*[?&]v=([^&\n?#]+)/,
    ];
    
    for (const pattern of patterns) {
      const match = urlOrId.match(pattern);
      if (match && match[1]) {
        return match[1];
      }
    }
    
    // If no pattern matches, return the original string (might be just an ID)
    return urlOrId;
  };

  // Show loading screen while checking authentication
  if (authLoading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
          <CircularProgress />
        </Box>
      </ThemeProvider>
    );
  }

  // Show loading screen while auth is loading
  if (authLoading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <CircularProgress />
        </Box>
      </ThemeProvider>
    );
  }

  // Admin analytics page — render when ?admin=true and user is logged in
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('admin') === 'true' && isAuthenticated) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AdminAnalytics />
      </ThemeProvider>
    );
  }

  if (window.location.pathname.toLowerCase() === '/privacy') {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <PrivacyPolicyPage />
      </ThemeProvider>
    );
  }

  if (window.location.pathname.toLowerCase() === '/delete-account') {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <DeleteAccountPage />
      </ThemeProvider>
    );
  }

  if (window.location.pathname.toLowerCase() === '/delete-data') {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <DeleteDataPage />
      </ThemeProvider>
    );
  }

  if (window.location.pathname.toLowerCase() === '/split-pdf') {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <SplitPdfPage />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      
      {/* Navigation */}
      <AppBar position="static" color="default" elevation={1}>
        <Toolbar>
          <Typography 
            variant="h6" 
            component="div" 
            sx={{ 
              flexGrow: 1, 
              cursor: 'pointer',
              '&:hover': {
                textDecoration: 'underline'
              }
            }}
            onClick={() => handleTabChange(0)}
          >
            Mobile Money Statement Analyzer
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip 
              title={currentTab === 0 && !isAuthenticated ? "Please log in to access the dashboard" : ""}
              placement="bottom"
            >
              <span>
                <Button 
                  color="inherit" 
                  onClick={() => handleTabChange(currentTab === 0 ? 1 : 0)}
                  disabled={false} // Always allow clicking to navigate
                  sx={{
                    transition: 'none',
                    '&:hover': {
                      transition: 'none',
                      transform: 'none'
                    },
                    '&:active': {
                      transition: 'none',
                      transform: 'none'
                    },
                    '&:focus': {
                      transition: 'none',
                      transform: 'none'
                    }
                  }}
                >
                  {currentTab === 0 ? 'Dashboard' : 'Analyzer'}
                </Button>
                
                {/* How It Works Tab Button */}
                <Button 
                  color="inherit" 
                  onClick={() => handleTabChange(2)}
                  sx={{
                    transition: 'none',
                    '&:hover': { transition: 'none', transform: 'none' },
                    '&:active': { transition: 'none', transform: 'none' },
                    '&:focus': { transition: 'none', transform: 'none' },
                  }}
                >
                  Guide
                </Button>

                {/* Plans Tab Button */}
                <Button 
                  color="inherit" 
                  onClick={() => handleTabChange(3)}
                  sx={{
                    transition: 'none',
                    '&:hover': { transition: 'none', transform: 'none' },
                    '&:active': { transition: 'none', transform: 'none' },
                    '&:focus': { transition: 'none', transform: 'none' },
                  }}
                >
                  Plans
                </Button>
                
                {/* Excel Templates Tab Button */}
                <Button 
                  color="inherit" 
                  onClick={() => handleTabChange(4)}
                  disabled={false}
                  sx={{
                    transition: 'none',
                    '&:hover': {
                      transition: 'none',
                      transform: 'none'
                    },
                    '&:active': {
                      transition: 'none',
                      transform: 'none'
                    },
                    '&:focus': {
                      transition: 'none',
                      transform: 'none'
                    }
                  }}
                >
                  Templates
                </Button>
              </span>
            </Tooltip>
            
            {/* Only show user menu for Dashboard/Subscription/Templates tab or when authenticated */}
            {(currentTab === 1 || currentTab === 3 || currentTab === 4 || isAuthenticated) && (
              <IconButton
                onClick={(e) => setAnchorEl(e.currentTarget)}
                color="inherit"
              >
                {user ? (
                  <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                    {getDisplayInitial()}
                  </Avatar>
                ) : (
                  <AccountCircle />
                )}
              </IconButton>
            )}
            
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => setAnchorEl(null)}
            >
              {user && (
                <MenuItem>
                  <Typography variant="body2">
                    Welcome, {getDisplayName()}
                  </Typography>
                </MenuItem>
              )}
              <MenuItem onClick={logout}>
                <Logout sx={{ mr: 1 }} />
                Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {currentTab === 0 ? (
        // Analyzer Tab - No authentication required
        <Box sx={{ minHeight: '100vh', background: theme.palette.background.default }}>
          <Container maxWidth="md" sx={{ py: 6 }}>
            {/* Add statement above file upload button */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary">
                By uploading a statement, you confirm you understand and accept our{' '}
                <Box component="a" href="/privacy" sx={{ color: '#388e3c', textDecoration: 'underline', display: 'inline' }}>Privacy Policy</Box>
                {' '}and{' '}
                <Box component="span" onClick={() => setTermsOpen(true)} sx={{ color: '#388e3c', textDecoration: 'underline', cursor: 'pointer' }}>Terms of Use</Box>.
              </Typography>
            </Box>
            {/* Header */}
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 4 }}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
                <img src="/app-Icon.png" alt="App Icon" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </Avatar>
              <Box>
                <Typography variant="h4" fontWeight={700} color="primary.main">Mobile Money Statement Analyzer</Typography>
                <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 1 }}>
                  Upload, filter, analyze and export your M-PESA, Airtel Money, KCB or Equity statements to Excel with ease.
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem', fontWeight: 500 }}>
                  Browse{' '}
                  <Box
                    component="span"
                    onClick={() => handleTabChange(4)}
                    sx={{
                      color: 'primary.main',
                      textDecoration: 'underline',
                      cursor: 'pointer',
                      fontWeight: 600,
                      fontSize: '1.15rem',
                      '&:hover': {
                        color: 'primary.dark'
                      }
                    }}
                  >
                    customizable Excel budgeting templates
                  </Box>
                  {' '}to use with your exported data.
                </Typography>
              </Box>
            </Stack>
            <MobileAppCta compact />
            {/* Sample PDF download links */}
            <Box>
              <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 1 }}>
                <a 
                  href="#how-to-use" 
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById('how-to-use')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                  style={{ color: '#388e3c', textDecoration: 'underline', cursor: 'pointer', marginRight: '8px' }}
                >
                  How to Use
                </a>
                | Test with sample statements:
              </Typography>
              <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <Button
                  component="a"
                  href="/Dummy_statement1.pdf"
                  download
                  variant="outlined"
                  size="small"
                  sx={{ 
                    color: 'primary.main',
                    borderColor: 'primary.main',
                    '&:hover': {
                      borderColor: 'primary.dark',
                      backgroundColor: 'primary.light',
                      color: 'primary.dark'
                    }
                  }}
                >
                  Download Sample PDF 1
                </Button>
                <Button
                  component="a"
                  href="/Dummy_statement2.pdf"
                  download
                  variant="outlined"
                  size="small"
                  sx={{ 
                    color: 'primary.main',
                    borderColor: 'primary.main',
                    '&:hover': {
                      borderColor: 'primary.dark',
                      backgroundColor: 'primary.light',
                      color: 'primary.dark'
                    }
                  }}
                >
                  Download Sample PDF 2
                </Button>
              </Stack>
            </Box>
            {/* Mode selector */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                What would you like to do?
              </Typography>
              <ToggleButtonGroup
                value={analyzerMode}
                exclusive
                onChange={(e, val) => val != null && setAnalyzerMode(val)}
                fullWidth
                size="small"
                disabled={loading || splitLoading}
                sx={{
                  flexWrap: 'wrap',
                  gap: 0.5,
                  '& .MuiToggleButton-root': { py: 1.25, flex: '1 1 140px' },
                }}
              >
                <ToggleButton value="analysis" aria-label="full analysis">
                  <Analytics sx={{ mr: 1, fontSize: 20 }} />
                  Full Analysis
                </ToggleButton>
                <ToggleButton value="convert" aria-label="convert to excel">
                  <TableChart sx={{ mr: 1, fontSize: 20 }} />
                  Convert
                </ToggleButton>
                <ToggleButton value="split" aria-label="split pdf">
                  <ContentCut sx={{ mr: 1, fontSize: 20 }} />
                  Split PDF
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
            {/* Upload & Filters Card */}
            <Card sx={{ p: { xs: 1.5, sm: 3 }, mb: 4, boxShadow: 3 }}>
              {analyzerMode === 'split' && (
                <Alert severity="info" sx={{ mb: 2, fontSize: '0.85rem' }}>
                  <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                    Why split first?
                  </Typography>
                  <Typography variant="body2" color="text.primary">
                    {ANALYZER_SPLIT_PDF_GUIDANCE}
                  </Typography>
                </Alert>
              )}
              {splitError && analyzerMode === 'split' && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSplitError('')}>
                  {splitError}
                </Alert>
              )}
              {splitSuccess && analyzerMode === 'split' && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSplitSuccess('')}>
                  {splitSuccess}
                </Alert>
              )}
              <Grid container spacing={3} alignItems="stretch">
                <Grid item xs={12} md={5}>
                  <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1, fontWeight: 700 }}>
                    Step 1 — Upload
                  </Typography>
                  <Stack spacing={2} sx={{ mt: 1 }}>
                    {analyzerMode === 'split' ? (
                      <Button variant="contained" component="label" fullWidth sx={{ minHeight: 52 }}>
                        Choose one PDF (max 100MB)
                        <input
                          type="file"
                          accept="application/pdf"
                          hidden
                          onChange={(e) => {
                            setSplitPdfFile(e.target.files?.[0] || null);
                            setSplitError('');
                            setSplitSuccess('');
                            e.target.value = '';
                          }}
                        />
                      </Button>
                    ) : (
                      <Button variant="contained" component="label" fullWidth sx={{ minHeight: 52 }}>
                        Upload PDF Files
                        <input type="file" accept="application/pdf" multiple hidden onChange={handleFileChange} />
                      </Button>
                    )}
                    {analyzerMode === 'split' && splitPdfFile && (
                      <Tooltip title={splitPdfFile.name}>
                        <Chip
                          label={splitPdfFile.name}
                          size="small"
                          sx={{ maxWidth: '100%', '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
                        />
                      </Tooltip>
                    )}
                    {analyzerMode !== 'split' && files.length > 0 && (
                      <Stack direction="row" flexWrap="wrap" gap={0.75} useFlexGap>
                        {files.map((file, index) => (
                          <Tooltip key={index} title={file.name}>
                            <Chip
                              label={file.name}
                              size="small"
                              variant="outlined"
                              sx={{ maxWidth: '100%', '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
                            />
                          </Tooltip>
                        ))}
                      </Stack>
                    )}
                    <TextField
                      label="PDF Password (if any)"
                      type="password"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      fullWidth
                      autoComplete="off"
                      variant="outlined"
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      helperText="M-PESA/KCB statements are often password-protected."
                    />
                  </Stack>
                </Grid>
                <Grid item xs={12} md={7}>
                  <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1, fontWeight: 700 }}>
                    Step 2 — {analyzerMode === 'split' ? 'Split settings' : analyzerMode === 'convert' ? 'Export settings' : 'Analysis filters'}
                  </Typography>
                  <Stack spacing={2} sx={{ mt: 1 }}>
                    {analyzerMode === 'analysis' && (
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth size="small">
                            <InputLabel id="period-label">Period</InputLabel>
                            <Select
                              labelId="period-label"
                              value={period}
                              label="Period"
                              onChange={e => setPeriod(e.target.value)}
                            >
                              <MenuItem value="all">All Time</MenuItem>
                              <MenuItem value="today">Today</MenuItem>
                              <MenuItem value="day">Select Day</MenuItem>
                              <MenuItem value="week">This Week</MenuItem>
                              <MenuItem value="month">This Month</MenuItem>
                              <MenuItem value="year">This Year</MenuItem>
                              <MenuItem value="custom">Custom</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth size="small">
                            <InputLabel id="paid-type-label">Paid Type</InputLabel>
                            <Select
                              labelId="paid-type-label"
                              value={paidType}
                              label="Paid Type"
                              onChange={e => setPaidType(e.target.value)}
                            >
                              <MenuItem value="paid_in">Paid In</MenuItem>
                              <MenuItem value="withdrawn">Withdrawn</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        {period === 'day' && (
                          <Grid item xs={12} sm={6}>
                            <DatePicker
                              label="Select Date"
                              value={selectedDate}
                              onChange={setSelectedDate}
                              renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                            />
                          </Grid>
                        )}
                        {period === 'custom' && (
                          <>
                            <Grid item xs={12} sm={6}>
                              <DatePicker
                                label="Start Date"
                                value={customStart}
                                onChange={setCustomStart}
                                renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <DatePicker
                                label="End Date"
                                value={customEnd}
                                onChange={setCustomEnd}
                                renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                              />
                            </Grid>
                          </>
                        )}
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="Min Amount"
                            type="number"
                            value={minAmount}
                            onChange={e => setMinAmount(e.target.value)}
                            fullWidth
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="Max Amount"
                            type="number"
                            value={maxAmount}
                            onChange={e => setMaxAmount(e.target.value)}
                            fullWidth
                            size="small"
                          />
                        </Grid>
                      </Grid>
                    )}
                    {analyzerMode === 'convert' && (
                      <FormControl fullWidth size="small">
                        <InputLabel id="convert-format-label">Output format</InputLabel>
                        <Select
                          labelId="convert-format-label"
                          value={convertFormat}
                          label="Output format"
                          onChange={e => setConvertFormat(e.target.value)}
                        >
                          <MenuItem value="excel">Excel (.xlsx)</MenuItem>
                          <MenuItem value="csv">CSV (.csv)</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                    {analyzerMode === 'split' && (
                      <TextField
                        label="Pages per file"
                        type="number"
                        value={splitPagesPerPart}
                        onChange={(e) => setSplitPagesPerPart(e.target.value)}
                        helperText={`Default ${ANALYZER_MAX_PAGES_PER_FILE} matches the per-upload analysis limit. Range 10–500.`}
                        fullWidth
                        size="small"
                        inputProps={{ min: 10, max: 500 }}
                      />
                    )}
                    {analyzerMode === 'analysis' && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic' }}>
                        Want to save analyses over time? Use the{' '}
                        <Box component="span" onClick={() => handleTabChange(1)} sx={{ color: 'primary.main', textDecoration: 'underline', cursor: 'pointer' }}>
                          Dashboard
                        </Box>
                        .
                      </Typography>
                    )}
                    {analyzerMode === 'split' && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic' }}>
                        Splitting does not use your page quota. After download, switch to Full Analysis or Convert for each part.
                      </Typography>
                    )}
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        if (analyzerMode === 'split') handleSplitPdf();
                        else if (analyzerMode === 'convert') handleConvert();
                        else handleAnalyze();
                      }}
                      disabled={
                        loading ||
                        splitLoading ||
                        (analyzerMode === 'split' && !splitPdfFile) ||
                        ((analyzerMode === 'analysis' || analyzerMode === 'convert') && (!files || files.length === 0))
                      }
                      fullWidth
                      sx={{ minHeight: 48 }}
                    >
                      {loading || splitLoading ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : analyzerMode === 'split' ? (
                        'Download ZIP of split PDFs'
                      ) : analyzerMode === 'convert' ? (
                        `Convert to ${convertFormat === 'csv' ? 'CSV' : 'Excel'}`
                      ) : (
                        'Analyze Statement'
                      )}
                    </Button>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        fontSize: '0.75rem',
                        fontStyle: 'italic',
                        opacity: 0.8,
                        textAlign: 'center',
                        display: 'block',
                      }}
                    >
                      {analyzerMode === 'split'
                        ? 'Choose one PDF, enter the password if needed, then download the ZIP.'
                        : analyzerMode === 'convert'
                          ? `Upload your PDF(s), then click Convert to ${convertFormat === 'csv' ? 'CSV' : 'Excel'}.`
                          : 'Upload your PDF(s), set filters, then click Analyze Statement.'}
                    </Typography>
                  </Stack>
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ mb: 2 }} />
                  <Stack spacing={1}>
                    {(!isAuthenticated || (isAuthenticated && !isPaidUser)) && (
                      <Alert severity="info" sx={{ py: 0.5, '& .MuiAlert-message': { py: 0.25 } }}>
                        <Typography variant="caption">
                          <strong>Usage limits:</strong> Guests — <strong>5 pages / 24h</strong>. Free accounts — <strong>20 pages / 24h, up to 100 pages / 30 days</strong>.
                          {!isAuthenticated && (
                            <> <Box component="span" onClick={() => handleTabChange(1)} sx={{ color: 'inherit', textDecoration: 'underline', cursor: 'pointer' }}>Create a free account</Box> for more.</>
                          )}
                          {isAuthenticated && !isPaidUser && (
                            <> <Box component="span" onClick={() => handleTabChange(3)} sx={{ color: 'inherit', textDecoration: 'underline', cursor: 'pointer' }}>Upgrade</Box> for higher limits.</>
                          )}
                        </Typography>
                      </Alert>
                    )}
                    {analyzerMode !== 'split' && (
                      <Alert severity="warning" sx={{ py: 0.5, '& .MuiAlert-message': { py: 0.25 } }}>
                        <Typography variant="caption">
                          Up to <strong>{ANALYZER_MAX_PAGES_PER_FILE} pages per PDF</strong> per upload for Full Analysis and Convert.
                          Longer statements? <Box component="span" onClick={() => setAnalyzerMode('split')} sx={{ fontWeight: 700, textDecoration: 'underline', cursor: 'pointer' }}>Switch to Split PDF</Box>
                          {' '}or <Box component="a" href="/split-pdf" sx={{ fontWeight: 700, color: 'inherit' }}>use the standalone page</Box>.
                        </Typography>
                      </Alert>
                    )}
                    <Alert
                      severity="success"
                      icon={<CardGiftcard fontSize="inherit" />}
                      sx={{ py: 0.5, '& .MuiAlert-message': { py: 0.25 } }}
                      action={
                        <Button
                          color="inherit"
                          size="small"
                          onClick={() => handleTabChange(3)}
                          sx={{ whiteSpace: 'nowrap' }}
                        >
                          View Program
                        </Button>
                      }
                    >
                      <Typography variant="caption">
                        <strong>Referral rewards:</strong> Earn bonus pages when friends sign up and buy a paid plan.
                      </Typography>
                    </Alert>
                  </Stack>
                </Grid>
              </Grid>
            </Card>
            {/* Progress Bar */}
            {(loading || splitLoading) && (
              <Card sx={{ p: 3, mb: 3, boxShadow: 3 }}>
                <Box sx={{ width: '100%', mb: 2 }}>
                  <LinearProgress 
                    sx={{ 
                      height: 8, 
                      borderRadius: 4,
                      backgroundColor: 'rgba(0, 0, 0, 0.1)',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 4,
                      }
                    }} 
                  />
                </Box>
                <Typography variant="body2" color="text.secondary" align="center">
                  {progressMessage ||
                    (splitLoading ? 'Preparing split PDFs…' : 'Processing your statement...')}
                </Typography>
                <Typography variant="caption" color="text.secondary" align="center" sx={{ display: 'block', mt: 1 }}>
                  {splitLoading
                    ? 'Building your ZIP. This step does not use analysis page quota.'
                    : `Please wait while we analyze your PDF. ${LARGE_PDF_LOADING_HINT}`}
                </Typography>
              </Card>
            )}
            {/* Error Alert */}
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {analyzerMode === 'convert' && conversionNotice && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Stack direction="row" spacing={2} alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" sx={{ width: '100%' }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" fontWeight="bold" color="text.primary" gutterBottom>
                      Partial conversion ({conversionNotice.pagesProcessed}/{conversionNotice.pagesTotal} pages in this run)
                    </Typography>
                    <Typography variant="body2" color="text.primary">
                      {conversionNotice.message}
                    </Typography>
                  </Box>
                  {conversionNotice.limitReason === 'server_cap' ? (
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={() => {
                        setAnalyzerMode('split');
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                    >
                      Split PDF
                    </Button>
                  ) : !isAuthenticated ? (
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={() => handleTabChange(1)}
                    >
                      Register Free (20 pages/day)
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={() => handleTabChange(3)}
                    >
                      Upgrade / Buy Day Pass
                    </Button>
                  )}
                </Stack>
              </Alert>
            )}
            {/* Analysis Results Section - only show when in analysis mode */}
            {result && analyzerMode === 'analysis' && (
              <Box id="analysis-results" sx={{ position: 'relative', zIndex: 1, mb: 4 }}>
                {/* Partial Results Banner - Show when quota limited the analysis */}
                {result.partial_result && result.partial_result.is_partial && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Stack direction="row" spacing={2} alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" sx={{ width: '100%' }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" fontWeight="bold" color="text.primary" gutterBottom>
                          Partial results ({result.partial_result.pages_processed}/{result.partial_result.total_pages} pages in this run)
                        </Typography>
                        <Typography variant="body2" color="text.primary" sx={{ mb: 0.5 }}>
                          {result.partial_result.upgrade_message ||
                            `Showing ${result.partial_result.transactions_shown.toLocaleString()} of ~${result.partial_result.estimated_total_transactions.toLocaleString()} estimated transactions.`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Summary: ~{result.partial_result.estimated_total_transactions.toLocaleString()} transactions estimated in the full PDF · {result.partial_result.transactions_shown.toLocaleString()} in this result.
                        </Typography>
                      </Box>
                      {result.partial_result.server_page_cap_applied ? (
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          onClick={() => {
                            setAnalyzerMode('split');
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }}
                        >
                          Split PDF
                        </Button>
                      ) : (
                        <Stack direction="row" spacing={1}>
                          {!isAuthenticated ? (
                            <Button
                              variant="contained"
                              color="primary"
                              size="small"
                              onClick={() => handleTabChange(1)}
                            >
                              Register Free (20 pages/day)
                            </Button>
                          ) : (
                            <Button
                              variant="contained"
                              color="primary"
                              size="small"
                              onClick={() => handleTabChange(3)}
                            >
                              Upgrade / Buy Day Pass
                            </Button>
                          )}
                        </Stack>
                      )}
                    </Stack>
                  </Alert>
                )}
                
                {result.processing_info && !result.partial_result?.is_partial && (
                  <Paper sx={{ p: 2, mb: 2, backgroundColor: 'primary.light', color: 'primary.contrastText' }}>
                    <Typography variant="body2" align="center">
                      Processed {result.processing_info.files_processed} files with {result.processing_info.total_transactions} transactions. 
                      {result.processing_info.duplicate_transactions_removed > 0 && ` ${result.processing_info.duplicate_transactions_removed} duplicate transactions removed.`}
                    </Typography>
                  </Paper>
                )}
                <Paper sx={{ p: 3, mb: 4 }}>
                  <Typography variant="h5" align="center" gutterBottom>
                    Total {paidType === 'paid_in' ? 'Paid In' : 'Withdrawn'}: {filteredTotal.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 1 }}>
                    The chart below shows only the top 10 people/businesses by total amount. To see all transactions, use the table below.
                  </Typography>
                  <Stack direction="row" justifyContent="center" sx={{ mb: 2 }}>
                    <ToggleButtonGroup
                      value={chartGranularity}
                      exclusive
                      onChange={(e, val) => { if (val) setChartGranularity(val); }}
                      size="small"
                    >
                      <ToggleButton value="daily">Daily</ToggleButton>
                      <ToggleButton value="monthly">Monthly</ToggleButton>
                      <ToggleButton value="yearly">Yearly</ToggleButton>
                    </ToggleButtonGroup>
                  </Stack>
                  <Stack 
                    direction="row" 
                    spacing={{ xs: 1, sm: 4 }} 
                    justifyContent="center" 
                    alignItems="center"
                    sx={{ mb: 2, flexWrap: 'wrap' }}
                  >
                    <Typography variant="caption" color="success.main" sx={{ textAlign: { xs: 'center', sm: 'left' }, fontSize: '0.75rem' }}>
                      <Tooltip title="Average daily amount across all dates in the filtered data">
                        <span>
                          Daily (Avg.): <b>{dayAvg.toLocaleString(undefined, { maximumFractionDigits: 0 })}</b>
                        </span>
                      </Tooltip>
                    </Typography>
                    <Typography variant="caption" color="success.main" sx={{ textAlign: { xs: 'center', sm: 'left' }, fontSize: '0.75rem' }}>
                      <Tooltip title="Average weekly amount across all weeks in the filtered data">
                        <span>
                          Weekly (Avg.): <b>{weekAvg.toLocaleString(undefined, { maximumFractionDigits: 0 })}</b>
                        </span>
                      </Tooltip>
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 600, textAlign: { xs: 'center', sm: 'left' }, fontSize: '0.75rem' }}>
                      <Tooltip title="Average monthly amount across all months in the filtered data">
                        <span>
                          Monthly (Avg.): <b>{monthAvg.toLocaleString(undefined, { maximumFractionDigits: 0 })}</b>
                        </span>
                      </Tooltip>
                    </Typography>
                  </Stack>
                  <Box sx={{ height: { xs: 250, sm: 500 } }}>
                    <Bar data={chartJsBarData} options={chartJsBarOptions} />
                  </Box>
                </Paper>
                
                {/* Top People/Businesses Section */}
                <Paper sx={{ p: 3, mb: 4, position: 'relative', zIndex: 1 }}>
                  <Typography variant="h6" gutterBottom>Top People/Businesses</Typography>
                  <Autocomplete
                    multiple
                    freeSolo
                    options={allEntityNames}
                    value={selectedEntities}
                    onChange={(e, newValue) => setSelectedEntities(newValue)}
                    inputValue={search}
                    onInputChange={(e, newValue) => setSearch(newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Search People/Business"
                        variant="outlined"
                        size="small"
                        fullWidth
                        sx={{ mb: 2 }}
                      />
                    )}
                  />
                  <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                    <Button variant="outlined" onClick={handleExportExcel}>
                      Export to Excel
                    </Button>
                    <Button variant="outlined" onClick={handleDownloadChart}>
                      Download Chart
                    </Button>
                  </Stack>
                  <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                    <List>
                      {filteredTopEntities.map((entity, idx) => {
                        const color = COLORS[idx % COLORS.length];
                        // Calculate percentage of total
                        const totalSum = filteredTopEntities.reduce((sum, e) => sum + e.total, 0);
                        const percent = totalSum > 0 ? (entity.total / totalSum) * 100 : 0;
                        return (
                          <ListItem key={idx} divider sx={{ borderLeft: `8px solid ${color}` }}>
                            <ListItemText
                              primary={entity.details}
                              secondary={`${entity.total.toLocaleString()} (${percent.toFixed(1)}%)`}
                            />
                          </ListItem>
                        );
                      })}
                    </List>
                  </Box>
                </Paper>
                
                {/* Transactions Table Section */}
                <Paper sx={{ p: 3, mb: 4, position: 'relative', zIndex: 1 }}>
                  <Typography variant="h6" gutterBottom>Transactions</Typography>
                  <Box sx={{ height: 400, width: '100%', overflow: 'hidden', mb: 2 }}>
                    <DataGrid
                      rows={filteredPreviewRows.map((row, idx) => {
                        const rowObj = { id: idx };
                        result.columns.forEach((col, colIdx) => {
                          rowObj[col] = row[colIdx];
                        });
                        return rowObj;
                      })}
                      columns={columns}
                      pageSize={10}
                      rowsPerPageOptions={[10, 25, 50, 100]}
                      disableSelectionOnClick
                      sx={{
                        '& .MuiDataGrid-cell': {
                          fontSize: '0.875rem',
                        },
                        '& .MuiDataGrid-columnHeader': {
                          fontSize: '0.875rem',
                          fontWeight: 600,
                        },
                      }}
                    />
                  </Box>
                </Paper>
              </Box>
            )}
            {/* How to Use Section */}
            <Card id="how-to-use" sx={{ p: { xs: 2, sm: 3 }, mb: 4, mt: 6, boxShadow: 3, backgroundColor: '#f8f9fa', position: 'relative', zIndex: 0 }}>
              <Typography variant="h5" fontWeight={600} color="primary.main" sx={{ mb: 3 }}>
                How to Use
              </Typography>
              
              <Typography variant="h6" sx={{ mb: 2, mt: 2 }}>Downloading Your M-Pesa Statement</Typography>
              
              <Stack spacing={3} sx={{ mb: 3 }}>
                <Box>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
                    Method 1: Using My OneApp
                  </Typography>
                  <Box component="ol" sx={{ pl: 3, m: 0, '& li': { mb: 1 } }}>
                    <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                      View Statements under "M-Pesa Balance"
                    </Typography>
                    <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                      Select "Statement Options" on the bottom right of the page
                    </Typography>
                    <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                      Select "Export Statement"
                    </Typography>
                    <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                      Choose the "Start Date" and "End Date", then select "Send via E-mail"
                    </Typography>
                    <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                      Select "Generate Statement", then enter your E-mail address and select "Continue"
                    </Typography>
                    <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                      Select "Proceed" to send it to your E-mail address
                    </Typography>
                    <Typography component="li" variant="body2">
                      Check your SMS messages for the password required to open the file
                    </Typography>
                  </Box>
                </Box>
                
                <Box>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
                    Method 2: Using USSD Code *234#
                  </Typography>
                  <Box component="ol" sx={{ pl: 3, m: 0, '& li': { mb: 1 } }}>
                    <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                      Dial *234# on your phone
                    </Typography>
                    <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                      Look for 'M-PESA Information' and follow the prompts to request your statement
                    </Typography>
                    <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                      You will receive an email with the PDF file attached
                    </Typography>
                    <Typography component="li" variant="body2">
                      Download the PDF file from the email and check your SMS for the password
                    </Typography>
                  </Box>
                </Box>
              </Stack>
              
              <Divider sx={{ my: 3 }} />
              
              {/* YouTube Tutorial Video Section */}
              <Box>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                  Video Tutorial
                </Typography>
                {youtubeVideoId && (
                  <>
                    {isMobile ? (
                      // Mobile: Show a button that opens YouTube directly
                      <Box
                        component="a"
                        href={youtubeVideoId.startsWith('http') ? youtubeVideoId : `https://youtu.be/${extractYouTubeId(youtubeVideoId)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          display: 'block',
                          width: '100%',
                          mb: 2,
                          textDecoration: 'none',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          backgroundColor: '#000',
                          position: 'relative',
                          paddingBottom: '56.25%',
                          '&:hover': {
                            opacity: 0.9
                          }
                        }}
                      >
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: 'white',
                            p: 3,
                            textAlign: 'center'
                          }}
                        >
                          <Typography variant="h5" sx={{ mb: 1, fontWeight: 700 }}>
                            ▶ Watch Tutorial
                          </Typography>
                          <Typography variant="body2" sx={{ opacity: 0.9 }}>
                            Tap to open in YouTube
                          </Typography>
                        </Box>
                      </Box>
                    ) : (
                      // Desktop: Show embedded video
                      <Box 
                        sx={{ 
                          position: 'relative', 
                          width: '100%', 
                          paddingBottom: '56.25%', 
                          mb: 2,
                          overflow: 'hidden',
                          borderRadius: '8px',
                          backgroundColor: '#000',
                          // Force GPU acceleration for mobile scrolling
                          transform: 'translateZ(0)',
                          willChange: 'transform',
                          backfaceVisibility: 'hidden',
                          WebkitBackfaceVisibility: 'hidden'
                        }}
                      >
                        <iframe
                          width="100%"
                          height="100%"
                          src={`https://www.youtube.com/embed/${extractYouTubeId(youtubeVideoId)}?playsinline=1&rel=0&modestbranding=1&enablejsapi=1&origin=${window.location.origin}`}
                          title="YouTube tutorial video"
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          allowFullScreen
                          loading="lazy"
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            borderRadius: '8px',
                            border: 'none',
                            // Ensure iframe stays visible during scroll on mobile
                            transform: 'translateZ(0)',
                            WebkitTransform: 'translateZ(0)',
                            pointerEvents: 'auto',
                            zIndex: 1
                          }}
                        />
                      </Box>
                    )}
                  </>
                )}
              </Box>
            </Card>
          </Container>
          <Footer onTermsClick={() => setTermsOpen(true)} />
        </Box>
      ) : currentTab === 1 ? (
        // Dashboard Tab - Requires authentication
        isAuthenticated ? (
          emailVerificationLoading ? (
            <Box sx={{ minHeight: '100vh', background: theme.palette.background.default, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Container maxWidth="sm">
                <Box display="flex" flexDirection="column" alignItems="center" gap={3}>
                  <CircularProgress size={60} />
                  <Typography variant="h6" color="text.secondary">
                    Verifying email confirmation...
                  </Typography>
                </Box>
              </Container>
            </Box>
          ) : (
            <Dashboard />
          )
        ) : (
          <Box sx={{ minHeight: '100vh', background: theme.palette.background.default, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Container maxWidth="sm">
              <Login onTabChange={handleTabChange} />
            </Container>
          </Box>
        )
      ) : currentTab === 2 ? (
        // How It Works Tab - Accessible to everyone
        <HowItWorks />
      ) : currentTab === 3 ? (
        // Plans Tab - Accessible to everyone
        isAuthenticated && emailVerificationLoading ? (
          <Box sx={{ minHeight: '100vh', background: theme.palette.background.default, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Container maxWidth="sm">
              <Box display="flex" flexDirection="column" alignItems="center" gap={3}>
                <CircularProgress size={60} />
                <Typography variant="h6" color="text.secondary">
                  Verifying email confirmation...
                </Typography>
              </Box>
            </Container>
          </Box>
        ) : (
          <Subscription onTabChange={handleTabChange} />
        )
      ) : currentTab === 4 ? (
        // Excel Templates Tab - Accessible to everyone
        <ExcelTemplates onTabChange={handleTabChange} />
      ) : null}

      {/* Terms of Use Modal */}
      <Dialog open={termsOpen} onClose={() => setTermsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Terms of Use
          <IconButton aria-label="close" onClick={() => setTermsOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>Effective Date: 2025-07-17</Typography>
          <Typography variant="body1" paragraph><b>By accessing and using this app, you agree to the following terms:</b></Typography>
          <Typography variant="body1" paragraph><b>1. No Affiliation</b><br />This tool is developed independently and is not affiliated with Safaricom, M-Pesa, Airtel Money, KCB, Equity or any financial institution.</Typography>
          <Typography variant="body1" paragraph><b>2. Acceptable Use</b><br />You are solely responsible for the use of your own statement and information.</Typography>
          <Typography variant="body1" paragraph><b>3. Intellectual Property</b><br />All code and original content in the app belongs to Winflamestech solutions.<br /><br />You may not duplicate or resell the tool without permission.</Typography>
          <Typography variant="body1" paragraph><b>4. Limitation of Liability</b><br />We are not liable for any damages, losses, or misinterpretations resulting from using the tool.<br /><br />Use is entirely at your own discretion.</Typography>
          <Typography variant="body1" paragraph><b>5. Updates and Changes</b><br />We may modify or suspend the service at any time without prior notice.<br /><br />Continued use means acceptance of future changes.</Typography>
          <Typography variant="body1" paragraph><b>6. Contact</b><br />For feedback or questions, email: statementanalyzer@mobilemoneystatementanalyzer.com</Typography>
        </DialogContent>
      </Dialog>
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <MuiAlert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </MuiAlert>
      </Snackbar>

      {/* Floating WhatsApp + Feedback Buttons */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        {/* WhatsApp Button */}
        {!feedbackOpen && (
          <Tooltip title="Chat on WhatsApp">
            <Button
              variant="contained"
              component="a"
              href="https://wa.me/254700304802?text=Hi%2C%20I%20have%20a%20question%20about%20Mobile%20Money%20Statement%20Analyzer"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                borderRadius: '50%',
                minWidth: 48,
                width: 48,
                height: 48,
                boxShadow: 3,
                backgroundColor: '#25D366',
                '&:hover': { backgroundColor: '#1ebe57', boxShadow: 6, transform: 'scale(1.05)' },
                transition: 'all 0.3s ease',
                p: 0,
              }}
            >
              <svg viewBox="0 0 24 24" width="26" height="26" fill="#fff">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
            </Button>
          </Tooltip>
        )}
        {/* Feedback Chat Button */}
        {!feedbackOpen ? (
          <Tooltip title="Send us feedback">
            <Button
              variant="contained"
              color="primary"
              onClick={() => setFeedbackOpen(true)}
              sx={{
                borderRadius: '50%',
                minWidth: 56,
                width: 56,
                height: 56,
                boxShadow: 3,
                '&:hover': {
                  boxShadow: 6,
                  transform: 'scale(1.05)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              <Chat />
            </Button>
          </Tooltip>
        ) : (
          <Card
            sx={{
              width: 360,
              maxWidth: 'calc(100vw - 48px)',
              boxShadow: 6,
              borderRadius: 2,
            }}
          >
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">Send Feedback</Typography>
              <IconButton size="small" onClick={() => setFeedbackOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Box>
            <Box sx={{ p: 2 }}>
              <TextField
                multiline
                rows={4}
                fullWidth
                placeholder="Share your thoughts, suggestions, or report issues..."
                value={feedbackMessage}
                onChange={(e) => setFeedbackMessage(e.target.value)}
                variant="outlined"
                disabled={feedbackSubmitting}
                sx={{ mb: 2 }}
              />
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  onClick={() => {
                    setFeedbackOpen(false);
                    setFeedbackMessage('');
                  }}
                  disabled={feedbackSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSubmitFeedback}
                  disabled={feedbackSubmitting || !feedbackMessage.trim()}
                  startIcon={feedbackSubmitting ? <CircularProgress size={16} /> : <Send />}
                >
                  {feedbackSubmitting ? 'Sending...' : 'Send'}
                </Button>
              </Stack>
            </Box>
          </Card>
        )}
      </Box>
    </ThemeProvider>
  );
}

function Footer({ onTermsClick }) {
  return (
    <Box component="footer" sx={{ mt: 6, py: 2, textAlign: 'center', borderTop: '1px solid #e0e0e0', color: 'text.secondary', background: '#fafafa' }}>
      <Typography variant="body2">
        © {new Date().getFullYear()} Mobile Money Statement Analyzer &nbsp;|&nbsp;
        <Box component="a" href="/privacy" sx={{ color: '#388e3c', textDecoration: 'underline', display: 'inline' }}>Privacy Policy</Box>
        &nbsp;|&nbsp;
        <Box component="a" href="/delete-account" sx={{ color: '#388e3c', textDecoration: 'underline', display: 'inline' }}>Delete account</Box>
        &nbsp;|&nbsp;
        <Box component="a" href="/delete-data" sx={{ color: '#388e3c', textDecoration: 'underline', display: 'inline' }}>Delete data</Box>
        &nbsp;|&nbsp;
        <Box component="a" href="/split-pdf" sx={{ color: '#388e3c', textDecoration: 'underline', display: 'inline' }}>Split PDF</Box>
        &nbsp;|&nbsp;
        <Box component="span" onClick={onTermsClick} sx={{ color: '#388e3c', textDecoration: 'underline', cursor: 'pointer' }}>Terms of Use</Box>
        &nbsp;|&nbsp;
        <Box component="a" href="https://wa.me/254700304802?text=Hi%2C%20I%20have%20a%20question%20about%20Mobile%20Money%20Statement%20Analyzer" target="_blank" rel="noopener noreferrer" sx={{ color: '#25D366', textDecoration: 'underline', cursor: 'pointer' }}>Chat on WhatsApp</Box>
      </Typography>
    </Box>
  );
}

export default App;